/**
 * Living-bar / panel pill bucket — discoverability glue (client-only).
 *
 * Source: page-voice embedding pool for THIS URL (up to PAGE_QUESTION_POOL_MAX).
 * Never canned starters; never FM answer chips.
 *
 * Display window: MAX_PAGE_PILLS (3) unasked at a time. When one is asked it is
 * removed session-wide and the next unasked from the pool refills the slot so
 * the visitor keeps seeing fresh page questions until the pool is exhausted.
 *
 * Roles:
 *   1. Open panel / under-bar: top unasked from the page pool.
 *   2. After answer-only: refill remaining unasked (same pool).
 *   3. Filter engine context_next_ask chips (belt + suspenders).
 *
 * Session-wide asked set (sessionStorage): exact + canonical near-dupe.
 * When none remain → no pills (free-type composer only).
 */

import type { TypewriterLine } from "./typewriter.ts";

export type PanelOpenSource = "bar_submit" | "bar_rest";

export const PAGE_PILLS_KIND = "page_question_pill" as const;

/** Max pills visible at once (display window). */
export const MAX_PAGE_PILLS = 3;

/** Max unique page embedding questions held as the refill pool. */
export const PAGE_QUESTION_POOL_MAX = 12;

/**
 * Soft ceiling only for pathological lengths (DOM safety). Normal page /
 * suggested questions are shown in full — no mid-question "…" for the visitor.
 */
export const PAGE_PILL_LABEL_MAX = 500;

/**
 * Message chrome that owns the footer instead of page suggested questions.
 * Minimal shape so bubble + tests can share one gate (no full ChatMessage type).
 */
export type PagePillChromeMessage = {
  sender?: string;
  text?: string;
  /** Any answerChips (answers or context_next_ask) own the footer over page FAQs. */
  answerChips?: { kind?: string } | null;
  cta?: unknown;
  chip?: unknown;
  form?: unknown;
};

/**
 * Hide client page Suggested when conversion chrome owns the footer.
 *
 * ANY engine answerChips (FM poles OR context_next_ask) suppress the client
 * page-pool FAQs. Dual chrome is FM row + engine context_next_ask row only —
 * never weak diversify FAQs under brain chips (that was the text/voice miss).
 *
 * Walks newest→oldest and stops at the latest agent prose turn so an older
 * ignored CTA does not permanently block pills after a later answer-only turn.
 */
export function shouldSuppressPagePills(
  messages: ReadonlyArray<PagePillChromeMessage>,
): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.cta || m.chip || m.form) return true;
    // FM poles + context_next_ask both claim footer over client page pool.
    if (m.answerChips && m.answerChips.kind !== PAGE_PILLS_KIND) return true;
    if (m.sender === "agent" && (m.text?.trim() ?? "").length > 0) return false;
  }
  return false;
}

/** Stable short hash for analytics (question text → hex). */
export function questionHash(q: string): string {
  const s = normalizeQuestion(q);
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export function normalizeQuestion(q: string): string {
  return q.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Multi-word brand/product phrases stripped for dual-phrasing collapse
 * (LLM emits branded + generic twins of the same intent).
 * Longest first so "blue hat ai" wins over "blue hat".
 */
const INTENT_STRIP_PHRASES = [
  "conversion intelligence",
  "traffic intelligence",
  "smart digital marketing",
  "rich lead handoff",
  "blue hat ai",
  "bluehatai",
  "blue hat",
  "bluehat",
] as const;

/** Adverbs / fillers that create false dual-phrasing in the index. */
const INTENT_STRIP_FILLERS =
  /\b(typically|usually|generally|really|actually|basically|simply|currently|often|just|also|still|ever|already|exactly|only)\b/gi;

/**
 * Aux / light verbs that flip dual-phrasing without changing intent
 * ("can show" ≈ "does show" ≈ "show").
 */
const INTENT_STRIP_AUX =
  /\b(can|could|does|do|did|will|would|should|may|might|is|are|was|were|has|have|had)\b/gi;

/**
 * Light content lemmas so embedding twins collapse:
 * "what visitors wanted" ≈ "what visitors were trying to understand".
 */
const INTENT_LEMMA: Record<string, string> = {
  shows: "show",
  showing: "show",
  showed: "show",
  reveal: "show",
  reveals: "show",
  revealing: "show",
  capture: "capture",
  captures: "capture",
  capturing: "capture",
  wanted: "intent",
  wants: "intent",
  wanting: "intent",
  needed: "intent",
  needs: "intent",
  needing: "intent",
  left: "leave",
  leaves: "leave",
  leaving: "leave",
  understand: "intent",
  understands: "intent",
  understanding: "intent",
  trying: "intent",
  try: "intent",
  limit: "limit",
  limits: "limit",
  missing: "missing",
  visitors: "visitor",
  visitor: "visitor",
  users: "user",
  user: "user",
  sessions: "session",
  events: "event",
  questions: "question",
  objections: "objection",
  analytics: "analytics",
  traditional: "traditional",
};

function intentTokens(q: string): string[] {
  let s = normalizeQuestion(q);
  s = s.replace(/[?!.,;:'"™®]+/g, " ");
  for (const phrase of INTENT_STRIP_PHRASES) {
    const re = new RegExp(`\\b${phrase.replace(/\s+/g, "\\s+")}\\b`, "gi");
    s = s.replace(re, " ");
  }
  s = s.replace(INTENT_STRIP_FILLERS, " ");
  s = s.replace(INTENT_STRIP_AUX, " ");
  // Light deixis / determiners / question scaffolding (not content).
  s = s.replace(
    /\b(the|a|an|this|that|these|those|your|our|my|their|its|same|what|how|why|when|where|who|which|me|you|about|with|from|into|than|compared|versus|vs|for|of|to|on|in|and|or|really|trying)\b/gi,
    " ",
  );
  s = s.replace(/[^a-z0-9$\s]/g, " ");
  const out: string[] = [];
  for (const raw of s.split(/\s+/)) {
    if (!raw || raw.length < 2) continue;
    const lem = INTENT_LEMMA[raw] ?? raw;
    if (lem.length < 2) continue;
    out.push(lem);
  }
  return out.sort();
}

/**
 * Soft intent key for page-question near-dupes (display + session asked).
 *
 * Collapses dual-phrasing twins from indexing:
 *   "Can BlueHatAI Conversion Intelligence show which traffic performed best?"
 *   "Can this show which traffic performed best?"
 *   "What does traditional analytics typically show?" ≈ "What does traditional analytics show?"
 *   "what visitors wanted" ≈ "what visitors were really trying to understand"
 *
 * Retrieval embeddings keep both strings; this key is UI/session only.
 */
export function canonicalQuestionKey(q: string): string {
  return intentTokens(q).join(" ");
}

/** Token Jaccard on soft intent tokens (0–1). */
export function questionIntentJaccard(a: string, b: string): number {
  const ta = new Set(intentTokens(a));
  const tb = new Set(intentTokens(b));
  if (ta.size === 0 || tb.size === 0) return 0;
  let hit = 0;
  for (const t of ta) if (tb.has(t)) hit += 1;
  return hit / (ta.size + tb.size - hit);
}

/** Prefer branded / more specific wording when two pills share an intent. */
export function preferDisplayQuestion(a: string, b: string): string {
  const brandScore = (q: string): number => {
    let n = 0;
    if (/\bblue\s*hat(?:\s*ai)?\b|\bbluehatai\b/i.test(q)) n += 2;
    if (/\bconversion intelligence\b|\btraffic intelligence\b/i.test(q)) n += 1;
    return n;
  };
  const sa = brandScore(a);
  const sb = brandScore(b);
  if (sa !== sb) return sa > sb ? a : b;
  return a.length >= b.length ? a : b;
}

/**
 * True when two questions are dual-phrasing / near-dupe of one intent.
 * Exact soft key match, or high Jaccard on content tokens.
 */
export function isSamePageQuestionIntent(a: string, b: string): boolean {
  const ca = canonicalQuestionKey(a);
  const cb = canonicalQuestionKey(b);
  if (!ca || !cb) return false;
  if (ca === cb) return true;
  // Very short keys are unsafe to merge (e.g. "show" alone).
  if (ca.length < 8 || cb.length < 8) return false;
  // One key is a token-subset of the other (typically / can vs does tails).
  const ta = ca.split(" ");
  const tb = cb.split(" ");
  if (ta.length >= 2 && tb.length >= 2) {
    const sa = new Set(ta);
    const sb = new Set(tb);
    const aInB = ta.every((t) => sb.has(t));
    const bInA = tb.every((t) => sa.has(t));
    if (aInB || bInA) return true;
  }
  return questionIntentJaccard(a, b) >= 0.55;
}

/**
 * Greedy diversity pick over a (usually focus-ranked) embedding list.
 * Never invents — only drops near-dupes / same-family as last user ask.
 * Prefer fewer distinct intents over three rephrases of one topic.
 */
export function pickDiversePageQuestions(
  questions: readonly string[],
  max: number,
  opts?: { againstLastUser?: string | null },
): string[] {
  if (max <= 0 || questions.length === 0) return [];
  const last = (opts?.againstLastUser ?? "").replace(/\s+/g, " ").trim();
  const out: string[] = [];

  const conflicts = (q: string): boolean => {
    if (last && isSamePageQuestionIntent(q, last)) return true;
    if (last && questionIntentJaccard(q, last) >= 0.55) return true;
    for (const o of out) {
      if (isSamePageQuestionIntent(o, q) || questionIntentJaccard(o, q) >= 0.55) {
        return true;
      }
    }
    return false;
  };

  for (const raw of questions) {
    if (out.length >= max) break;
    const t = raw.replace(/\s+/g, " ").trim();
    if (!t) continue;
    if (conflicts(t)) {
      // Twin of something already picked — maybe upgrade display wording.
      const i = out.findIndex(
        (o) => isSamePageQuestionIntent(o, t) || questionIntentJaccard(o, t) >= 0.55,
      );
      if (i >= 0) out[i] = preferDisplayQuestion(t, out[i]!);
      continue;
    }
    out.push(t);
  }

  // If everything collided with last user, still show one non-exact-string Q
  // so the rail is never blank when inventory exists.
  if (out.length === 0) {
    for (const raw of questions) {
      const t = raw.replace(/\s+/g, " ").trim();
      if (!t) continue;
      if (last && normalizeQuestion(t) === normalizeQuestion(last)) continue;
      out.push(t);
      break;
    }
  }
  return out;
}

/**
 * Session-wide set of page questions the visitor already asked (normalized).
 * Stores both exact + canonical keys. In-memory + sessionStorage so SPA nav
 * and same-tab reloads keep exclusion.
 */
const ASKED_PAGE_QS_KEY = "bhai_asked_page_questions";

const askedMemory = new Set<string>();
let memoryHydrated = false;

function hydrateMemoryFromStorage(): void {
  if (memoryHydrated) return;
  memoryHydrated = true;
  try {
    const raw = sessionStorage.getItem(ASKED_PAGE_QS_KEY);
    if (!raw) return;
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return;
    for (const x of arr) {
      if (typeof x === "string" && x.length > 0) askedMemory.add(x);
    }
  } catch {
    /* ignore */
  }
}

function persistMemory(): void {
  try {
    sessionStorage.setItem(ASKED_PAGE_QS_KEY, JSON.stringify([...askedMemory]));
  } catch {
    /* ignore */
  }
}

/** All asked keys (exact + canonical) this browsing session. */
export function getAskedPageQuestions(): Set<string> {
  hydrateMemoryFromStorage();
  return new Set(askedMemory);
}

/** Record one or more asked questions (exact + canonical keys). */
export function markPageQuestionAsked(text: string | readonly string[]): void {
  hydrateMemoryFromStorage();
  const list = typeof text === "string" ? [text] : text;
  let changed = false;
  for (const t of list) {
    const exact = normalizeQuestion(t);
    if (!exact) continue;
    if (!askedMemory.has(exact)) {
      askedMemory.add(exact);
      changed = true;
    }
    const can = canonicalQuestionKey(t);
    // Only store canonical when it still carries real content (avoid empty keys).
    if (can.length >= 8 && !askedMemory.has(can)) {
      askedMemory.add(can);
      changed = true;
    }
  }
  if (changed) persistMemory();
}

/** True if this question (exact or near-dupe) was already asked this session. */
export function wasPageQuestionAsked(text: string): boolean {
  hydrateMemoryFromStorage();
  const exact = normalizeQuestion(text);
  if (!exact) return false;
  if (askedMemory.has(exact)) return true;
  const can = canonicalQuestionKey(text);
  if (can.length >= 8 && askedMemory.has(can)) return true;
  // Paraphrase gate (rebuild of the fix un-shipped in the #388 revert): a
  // REWORDED just-asked question must never resurface as a fresh pill
  // ("what visitors needed before they leave" vs "what visitors wanted
  // before they left"). Zero-LLM — the same isSamePageQuestionIntent used
  // for candidate-vs-candidate dedup, applied to the session asked-set.
  // Voice reads the same filtered list, so this covers both surfaces.
  if (can.length >= 8) {
    for (const asked of askedMemory) {
      if (asked.length < 8) continue;
      if (isSamePageQuestionIntent(text, asked)) return true;
    }
  }
  return false;
}

/**
 * Filter grounded page questions, dropping already-asked (session).
 * Exact + intent near-dupe; collapses dual-phrasing twins within the list
 * (keeps the more branded wording when both appear).
 */
export function filterUnaskedPageQuestions(
  questions: readonly string[],
  max = MAX_PAGE_PILLS,
): string[] {
  hydrateMemoryFromStorage();
  const out: string[] = [];
  const seenExact = new Set<string>();
  const seenCanon = new Set<string>();
  for (const raw of questions) {
    const t = raw.replace(/\s+/g, " ").trim();
    if (!t) continue;
    if (wasPageQuestionAsked(t)) continue;
    const exact = normalizeQuestion(t);
    const can = canonicalQuestionKey(t);
    if (seenExact.has(exact)) continue;
    const twinIdx = out.findIndex((o) => isSamePageQuestionIntent(o, t));
    if (twinIdx >= 0) {
      out[twinIdx] = preferDisplayQuestion(t, out[twinIdx]!);
      continue;
    }
    if (can.length >= 8 && seenCanon.has(can)) {
      // Twin already in the window — maybe upgrade to branded form.
      const i = out.findIndex((o) => canonicalQuestionKey(o) === can);
      if (i >= 0) out[i] = preferDisplayQuestion(t, out[i]!);
      continue;
    }
    seenExact.add(exact);
    if (can.length >= 8) seenCanon.add(can);
    out.push(t);
    if (out.length >= max) break;
  }
  return out;
}

/**
 * Filter chip options (context_next_ask / page pills) by session asked set.
 * Empty → caller should hide the suggested row (free-type only).
 */
export function filterUnaskedChipOptions<T extends { sendText: string }>(
  options: readonly T[],
  max = MAX_PAGE_PILLS,
): T[] {
  hydrateMemoryFromStorage();
  const out: T[] = [];
  const seenExact = new Set<string>();
  const seenCanon = new Set<string>();
  for (const opt of options) {
    const t = (opt.sendText ?? "").replace(/\s+/g, " ").trim();
    if (!t) continue;
    if (wasPageQuestionAsked(t)) continue;
    const exact = normalizeQuestion(t);
    const can = canonicalQuestionKey(t);
    if (seenExact.has(exact)) continue;
    const twinIdx = out.findIndex((o) => isSamePageQuestionIntent(o.sendText, t));
    if (twinIdx >= 0) {
      if (preferDisplayQuestion(t, out[twinIdx]!.sendText) === t) out[twinIdx] = opt;
      continue;
    }
    if (can.length >= 8 && seenCanon.has(can)) {
      const i = out.findIndex((o) => canonicalQuestionKey(o.sendText) === can);
      if (i >= 0 && preferDisplayQuestion(t, out[i]!.sendText) === t) {
        out[i] = opt;
      }
      continue;
    }
    seenExact.add(exact);
    if (can.length >= 8) seenCanon.add(can);
    out.push(opt);
    if (out.length >= max) break;
  }
  return out;
}

/** Clear session asked set (tests + ?bh_new_session=1). */
export function clearAskedPageQuestions(): void {
  askedMemory.clear();
  memoryHydrated = true;
  try {
    sessionStorage.removeItem(ASKED_PAGE_QS_KEY);
  } catch {
    /* ignore */
  }
}

/** @deprecated alias — use clearAskedPageQuestions */
export function clearAskedPageQuestionsForTests(): void {
  clearAskedPageQuestions();
}

/**
 * Page-pill / suggested-question display label — full question for the visitor.
 * Only ellipsizes pathological lengths (softMax), never normal corpus Qs.
 */
export function pillLabel(q: string, softMax = PAGE_PILL_LABEL_MAX): string {
  const t = q.replace(/\s+/g, " ").trim();
  if (t.length <= softMax) return t;
  return t.slice(0, softMax - 1).trimEnd() + "…";
}

/**
 * @deprecated Prefer pillLabel.
 */
export function shortPillLabel(q: string, max = PAGE_PILL_LABEL_MAX): string {
  return pillLabel(q, max);
}

type ExcludeArg = string | string[] | ReadonlySet<string> | undefined;

function toExcludeSet(exclude: ExcludeArg): Set<string> {
  const out = new Set<string>();
  if (exclude == null) return out;
  if (typeof exclude === "string") {
    const k = normalizeQuestion(exclude);
    if (k) out.add(k);
    const can = canonicalQuestionKey(exclude);
    if (can.length >= 12) out.add(can);
    return out;
  }
  for (const e of exclude) {
    const k = normalizeQuestion(e);
    if (k) out.add(k);
    const can = canonicalQuestionKey(e);
    if (can.length >= 12) out.add(can);
  }
  return out;
}

/**
 * Grounded page_question lines for the pill bucket. Max {@link MAX_PAGE_PILLS}.
 * Drops session-asked + caller exclude (exact + canonical near-dupe).
 */
export function pageQuestionsForPills(
  barLines: TypewriterLine[],
  exclude?: ExcludeArg,
): string[] {
  hydrateMemoryFromStorage();
  const excludeKeys = toExcludeSet(exclude);
  for (const k of askedMemory) excludeKeys.add(k);

  const texts = barLines
    .filter((line) => line.kind === "page_question")
    .map((line) => line.text.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  // Reuse filter but with extra exclude keys temporarily in memory path:
  const out: string[] = [];
  const seenExact = new Set<string>();
  const seenCanon = new Set<string>();
  for (const t of texts) {
    const exact = normalizeQuestion(t);
    const can = canonicalQuestionKey(t);
    if (excludeKeys.has(exact) || (can.length >= 12 && excludeKeys.has(can))) continue;
    if (wasPageQuestionAsked(t)) continue;
    if (seenExact.has(exact)) continue;
    if (can.length >= 12 && seenCanon.has(can)) {
      const i = out.findIndex((o) => isSamePageQuestionIntent(o, t));
      if (i >= 0) out[i] = preferDisplayQuestion(t, out[i]!);
      continue;
    }
    seenExact.add(exact);
    if (can.length >= 12) seenCanon.add(can);
    out.push(t);
    if (out.length >= MAX_PAGE_PILLS) break;
  }
  return out;
}

/**
 * Build the shared next-ask list for voice + Suggested chrome (single source of truth).
 *
 * Priority (token-safe):
 *   1. existing shared list (server bar / prior rank) — no new network
 *   2. rankedFromApi (one spend-gated rank max:3) — pick = ranked[0]
 *   3. lexical pools (client-only) — no network
 *
 * Voice speaks #1; panel paints the same strings. Never invents freeform text.
 */
export function buildSharedNextAsks(input: {
  existing?: readonly string[] | null;
  rankedFromApi?: readonly string[] | null;
  pick?: string | null;
  lexicalPools?: readonly (readonly string[])[];
  max?: number;
}): string[] {
  const max = input.max ?? MAX_PAGE_PILLS;
  const clean = (qs: readonly string[] | null | undefined): string[] => {
    if (!qs?.length) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of qs) {
      const t = (raw ?? "").replace(/\s+/g, " ").trim();
      if (t.length < 6) continue;
      const k = t.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(t);
      if (out.length >= max) break;
    }
    return out;
  };

  const fromExisting = clean(input.existing ?? null);
  if (fromExisting.length > 0) return fromExisting;

  const fromApi = clean(input.rankedFromApi ?? null);
  if (fromApi.length > 0) {
    const pick = (input.pick ?? "").replace(/\s+/g, " ").trim();
    if (pick.length >= 6 && !fromApi.some((q) => q.toLowerCase() === pick.toLowerCase())) {
      return [pick, ...fromApi].slice(0, max);
    }
    return fromApi;
  }

  const pools = input.lexicalPools ?? [];
  const flat: string[] = [];
  for (const pool of pools) {
    for (const q of pool) flat.push(q);
  }
  let lexical = clean(flat);
  const pick = (input.pick ?? "").replace(/\s+/g, " ").trim();
  if (pick.length >= 6 && !lexical.some((q) => q.toLowerCase() === pick.toLowerCase())) {
    lexical = [pick, ...lexical].slice(0, max);
  }
  return lexical;
}
